import { css, styled } from "@mui/material";
import { View } from "@novorender/api";
import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from "gl-matrix";
import { forwardRef, Fragment, SVGProps, useEffect, useImperativeHandle, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { CanvasHudLineLabel } from "components/canvasHud/lineLabel";
import {
    calcMinDimensionalDist,
    ensureLabelIsOnScreen,
    LineLabel,
    updateLineLabel2d,
} from "components/canvasHud/lineLabelMath";
import { OverlayMenuBox } from "components/canvasHud/overlayMenuBox";
import { OverlayMenuIconButton } from "components/canvasHud/overlayMenuIconButton";
import { featuresConfig } from "config/features";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample, useLastPickSample } from "contexts/lastPickSample";
import { getCameraDir } from "features/engine2D/utils";
import { PICK_TARGET_OFFSET } from "features/mobileMeasure/mobileMeasureSlice";
import { mobileMeasureActions, mobileMeasureCursorRef } from "features/mobileMeasure/mobileMeasureSlice";
import { CameraType, Picker, renderActions, selectCamera, selectPicker } from "features/render";
import { getSnapGuides2d, getSnapInfo2d, PointSnapInfo2d } from "features/render/measureSnap";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { useIsMobileMeasureInteraction } from "hooks/useIsMobileMeasureInteraction";
import { useMobileLongPress } from "hooks/useMobileLongPress";
import { useOpenWidget } from "hooks/useOpenWidget";
import { useRedirectWheelEvents } from "hooks/useRedirectWheelEvents";
import InfoIcon from "media/icons/info.svg?react";
import TrashIcon from "media/icons/trash.svg?react";
import { selectIsProcoreDesign, selectWidgets } from "slices/explorer";
import { getLineSegmentLength2d, getLineSlope, isPoint2dInsidePolygon, radToDeg } from "utils/math";
import { hasMouseSupport } from "utils/misc";

import { useMeasurePointToPointAxes } from "./hooks/useMeasurePointToPointAxes";
import { usePointToPointAxisDrawProduct } from "./hooks/usePointToPointAxisDrawProduct";
import { handleDeleteLineSegment, handleDeletePointLine } from "./pointLineDeleteHandlers";
import {
    PointLine,
    pointLineActions,
    selectDraggingPoint,
    selectPointLineCurrentIdx,
    selectPointLines,
    selectPointLineSnapToPoint,
    selectShowPointAngleOnCanvas,
    selectShowPointHeightOnCanvas,
} from "./pointLineSlice";
import { getPointLineWithDrag, isPointLineClosed } from "./utils";

const MENU_VISIBLITY_SQR_DIST = 40 ** 2;
const cursorColor = "#0F3";
const labelBgColor = "#000";
const labelTextColor = "#FFFFFF";
const pointLabelBgColor = "#848484";
const pointLabelTextColor = "#000000";

type LineLabelData = LineLabel & {
    text: string;
    /** When deletable (first/last segment, !closed), the id of the point that would be removed. */
    deletablePointId?: string;
};

type PointLabelData = {
    text: string | undefined;
    position2d: ReadonlyVec2 | undefined;
    angleText: string | undefined;
    anglePosition2d: ReadonlyVec2 | undefined;
};

type ComputedPointLine = {
    pointLine: PointLine;
    snapInfo2d: (PointSnapInfo2d | undefined)[];
    lineLabels: LineLabelData[];
    pointLabels: PointLabelData[] | undefined;
    points2d: (ReadonlyVec2 | undefined)[];
    isClosed: boolean;
    xAxisLabel?: LineLabelData;
    yAxisLabel?: LineLabelData;
    zAxisLabel?: LineLabelData;
    center: ReadonlyVec3;
    centerLabelsPos2d: ReadonlyVec2 | undefined;
    menuPos2d: ReadonlyVec2 | undefined;
    totalLengthStr: string;
};

export const PointToPointHtmlInteractions = forwardRef(function PointLineHtmlInteractions(_props, ref) {
    const {
        state: { view, size, canvas },
    } = useExplorerGlobals();

    const uom = useUom();
    const pointLines = useAppSelector(selectPointLines);
    const draggingPoint = useAppSelector(selectDraggingPoint);
    const picker = useAppSelector(selectPicker);
    const isPointLinePicker = picker === Picker.PointLine;
    const lastPickSampleRaw = useLastPickSample();
    const lastPickSample = isPointLinePicker ? lastPickSampleRaw : null;
    const dispatchLastPickSample = useDispatchLastPickSample();
    const showPointHeightOnCanvas = useAppSelector(selectShowPointHeightOnCanvas);
    const showPointAngleOnCanvas = useAppSelector(selectShowPointAngleOnCanvas);
    const currentIndex = useAppSelector(selectPointLineCurrentIdx);
    const snapTo = useAppSelector(selectPointLineSnapToPoint);

    const axisDrawProducts = usePointToPointAxisDrawProduct(view, pointLines);

    const orthoCameraDir = useOrthoCameraDir();

    const containerRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();
    const lastPointerPos = useRef<ReadonlyVec2 | null>(null);
    const hasMouse = hasMouseSupport();
    const isMobileMeasureInteraction = useIsMobileMeasureInteraction();
    /** Desktop-style point handlers (vs D-pad / long-press) — same condition as canvas measurement pickers. */
    const isDesktopPointLinePointerInteraction = !isMobileMeasureInteraction;

    // Point line canvas is also interested in results of this hook, but it's enough
    // to have it in a single place since it updates the store.
    useMeasurePointToPointAxes(view, pointLines);

    // When exiting point line mode and there's only a single point in the line,
    // clear the current point line because
    // - a single point in this tool is not that useful
    // - there's no way to remove a single point since we only allow removing line segments
    useEffect(() => {
        if (!isPointLinePicker) {
            dispatch(pointLineActions.clearCurrentIfSinglePoint());
        }
    }, [dispatch, isPointLinePicker]);

    // Reset temp info when point lines change
    useEffect(() => {
        dispatch(pointLineActions.setDraggingPoint(null));
        dispatch(pointLineActions.setPointLineSnapToPoint(null));
    }, [dispatch, pointLines]);

    // Need to have touch-action:none for point dragging to work on touch devices.
    // Can't enable it when start dragging, because it's too late.
    useEffect(() => {
        if (isPointLinePicker) {
            document.body.style.touchAction = "none";
        } else {
            document.body.style.removeProperty("touch-action");
        }
    }, [isPointLinePicker]);

    const computedPointLines: ComputedPointLine[] = useMemo(() => {
        if (!view) {
            return [];
        }

        const result = pointLines.map((pointLine, lineIdx) => {
            let points = pointLine.points;
            let measure = pointLine.result;

            const isClosed = isPointLineClosed(points);

            // If dragging a point in this line - use drag position
            let remeasure = false;
            if (lineIdx === draggingPoint?.pointLineIdx && lastPickSample?.position) {
                points = getPointLineWithDrag(
                    pointLine,
                    isClosed,
                    draggingPoint.pointIdx,
                    lastPickSample.position,
                    snapTo,
                );

                remeasure = true;

                pointLine = { ...pointLine, points, result: measure };
            }

            if (remeasure || !measure) {
                measure = view.measure?.core.measureLineStrip(points.map((p) => p.position));
            }

            // Add label in the middle of each line segment
            const lineLabels: LineLabelData[] = [];
            if (measure) {
                for (let i = 0; i < measure.linestrip.length - 1; i++) {
                    const start = measure.linestrip[i];
                    const end = measure.linestrip[i + 1];
                    const mid = vec3.lerp(vec3.create(), start, end, 0.5);
                    const slope = getLineSlope(start, end);

                    let text: string;
                    let slopeStr = "";
                    if (slope === Infinity) {
                        slopeStr = `, % ∞`;
                    } else if (slope >= 0.01) {
                        slopeStr = `, % ${slope.toFixed(2)}`;
                    }
                    const length3dStr = convertAndFormat(uom, measure?.segmentLengts[i], UnitType.length, {
                        decimals: 3,
                    });
                    if (orthoCameraDir) {
                        const length2d = getLineSegmentLength2d(start, end, orthoCameraDir);
                        const length2dStr = convertAndFormat(uom, length2d, UnitType.length, { decimals: 3 });
                        text = `${length2dStr} (3D: ${length3dStr}${slopeStr})`;
                    } else {
                        text = `${length3dStr}${slopeStr}`;
                    }

                    let deletablePointId: string | undefined;

                    // First segment
                    if (i === 0 && !isClosed) {
                        deletablePointId = points[0].id;
                    }
                    // Last segment. Deletable point is the last point. Num_points = num_segments + 1.
                    else if (i === measure.linestrip.length - 2 && !isClosed) {
                        deletablePointId = points[i + 1].id;
                    }

                    lineLabels.push({
                        text,
                        position: mid,
                        start,
                        end,
                        dist2d: 0,
                        deletablePointId,
                    });
                }
            }

            // Add labels for axis lines if available (only whene there are 2 points)
            let xAxisLabel: LineLabelData | undefined;
            let yAxisLabel: LineLabelData | undefined;
            let zAxisLabel: LineLabelData | undefined;
            if (
                axisDrawProducts[lineIdx] &&
                pointLine.firstTwoPointAxisMeasurements &&
                !(draggingPoint?.pointLineIdx === lineIdx)
            ) {
                const getAxis = (name: string, distance: number) => {
                    const axis = axisDrawProducts[lineIdx]?.objects[0].parts.find((p) => p.name === name);
                    if (!axis) {
                        return;
                    }
                    const text = convertAndFormat(uom, distance, UnitType.length, { decimals: 3 });
                    const [p1, p2] = axis.vertices3D;
                    return {
                        text,
                        position: vec3.lerp(vec3.create(), p1, p2, 0.5),
                        start: p1,
                        end: p2,
                        dist2d: 0,
                    };
                };
                xAxisLabel = getAxis("x-axis", pointLine.firstTwoPointAxisMeasurements.distanceX);
                yAxisLabel = getAxis("y-axis", pointLine.firstTwoPointAxisMeasurements.distanceY);
                zAxisLabel = getAxis("z-axis", pointLine.firstTwoPointAxisMeasurements.distanceZ);
            }

            // Exclude last point for closed lines, because it matches the first point
            const labelPoints = isClosed ? points.slice(0, -1) : points;

            // Put a label next to each point
            let pointLabels: PointLabelData[] | undefined;
            if (showPointAngleOnCanvas || showPointHeightOnCanvas) {
                pointLabels = labelPoints.map((point, idx) => {
                    const angle =
                        measure?.angles && (isClosed && idx === 0 ? measure.angles.at(-1) : measure.angles[idx - 1]);

                    return {
                        text: showPointHeightOnCanvas
                            ? `Z: ${convertAndFormat(uom, point.position[2], UnitType.length, { decimals: 3 })}`
                            : undefined,
                        angleText:
                            measure && showPointAngleOnCanvas && angle !== undefined
                                ? convertAndFormat(uom, radToDeg(angle), UnitType.angle)
                                : undefined,
                        position2d: undefined,
                        anglePosition2d: undefined,
                    };
                });
            }

            const center = vec3.create();
            if (labelPoints.length > 0) {
                labelPoints.forEach((point) => {
                    vec3.add(center, center, point.position);
                });
                vec3.scale(center, center, 1 / labelPoints.length);
            }

            return {
                pointLine,
                snapInfo2d: [],
                lineLabels,
                pointLabels,
                points2d: [],
                isClosed,
                xAxisLabel,
                yAxisLabel,
                zAxisLabel,
                center,
                centerLabelsPos2d: undefined,
                menuPos2d: undefined,
                totalLengthStr: convertAndFormat(uom, measure?.totalLength ?? 0, UnitType.length, { decimals: 3 }),
            };
        });
        updateComputedPointLines2d(view, size.width, size.height, result);
        return result;
    }, [
        view,
        pointLines,
        uom,
        draggingPoint,
        lastPickSample,
        axisDrawProducts,
        orthoCameraDir,
        size,
        snapTo,
        showPointHeightOnCanvas,
        showPointAngleOnCanvas,
    ]);

    useEffect(() => {
        if (!canvas || !hasMouse) {
            return;
        }

        // Update line menu (the one in the middle with delete button) based on pointer position
        function handlePointerMove(event: PointerEvent) {
            const container = containerRef.current;
            if (!container) {
                return;
            }

            const pos = vec2.fromValues(event.offsetX, event.offsetY);
            lastPointerPos.current = pos;

            computedPointLines.forEach((line, lineIndex) => {
                const visible = getMenuVisibility(pos, line, !!draggingPoint);
                toggleMenuVisibility(container, lineIndex, visible);
                toggleCenterLabelsVisibility(container, lineIndex, !visible);
            });
        }

        canvas.addEventListener("pointermove", handlePointerMove);
        return () => {
            canvas.removeEventListener("pointermove", handlePointerMove);
        };
    }, [computedPointLines, canvas, hasMouse, dispatch, draggingPoint]);

    useImperativeHandle(
        ref,
        () => ({
            update: () => {
                if (!view) {
                    return;
                }

                updateComputedPointLines2d(view, size.width, size.height, computedPointLines);
                if (containerRef.current) {
                    updateElementPositions(
                        computedPointLines,
                        containerRef.current,
                        hasMouseSupport() ? lastPointerPos.current : null,
                        Boolean(draggingPoint),
                    );
                }
            },
        }),
        [view, computedPointLines, size, draggingPoint],
    );

    const onWheel = useRedirectWheelEvents();

    useEffect(() => {
        updateElementPositions(
            computedPointLines,
            containerRef.current!,
            hasMouseSupport() ? lastPointerPos.current : null,
            Boolean(draggingPoint),
        );
    }, [computedPointLines, draggingPoint]);

    const teleportDpadToPoint = (pointLineIndex: number, pointIndex: number) => {
        if (!view) {
            return;
        }
        const line = pointLines[pointLineIndex];
        if (!line) {
            return;
        }
        const point = line.points[pointIndex];
        if (!point) {
            return;
        }
        const screenPos = view.measure?.draw.toMarkerPoints([point.position]);
        if (screenPos?.[0]) {
            const dpadX = screenPos[0][0] - PICK_TARGET_OFFSET.x;
            const dpadY = screenPos[0][1] - PICK_TARGET_OFFSET.y;
            dispatch(mobileMeasureActions.setCursorPosition({ x: dpadX, y: dpadY }));
            dispatch(mobileMeasureActions.setIndicatorPosition({ x: screenPos[0][0], y: screenPos[0][1] }));
            dispatch(mobileMeasureActions.setCursorActive(true));
        }
    };

    const handleMobilePointTap = (pointLineIndex: number, pointIndex: number) => {
        if (!view) {
            return;
        }
        const line = pointLines[pointLineIndex];
        if (!line) {
            return;
        }

        if (pointIndex === 0) {
            dispatch(pointLineActions.reversePoints(pointLineIndex, view));
        }

        mobileMeasureCursorRef.movingPoint = null;
        mobileMeasureCursorRef.movingPointOriginal = null;
        dispatch(mobileMeasureActions.clearMovingPoint());
        dispatch(pointLineActions.setCurrent(pointLineIndex));
        teleportDpadToPoint(pointLineIndex, pointIndex);
        dispatchLastPickSample(lastPickSampleActions.set(null));
        dispatch(mobileMeasureActions.setIndicatorTransform(null));
        dispatch(mobileMeasureActions.setDpadStyle("transparent"));
        dispatch(mobileMeasureActions.setPointLineMode("transparent"));
        dispatch(mobileMeasureActions.setActivelyDrawing(false));
        dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
    };

    const handleMobileLongPress = (pointLineIndex: number, pointIndex: number) => {
        if (!view) {
            return;
        }
        const line = pointLines[pointLineIndex];
        if (!line) {
            return;
        }

        const pt = line.points[pointIndex];
        mobileMeasureCursorRef.movingPointOriginal = pt
            ? {
                  position: [...pt.position] as [number, number, number],
                  snap: pt.snap,
                  objectId: pt.objectId,
                  id: pt.id,
              }
            : null;
        dispatch(pointLineActions.setCurrent(pointLineIndex));
        dispatch(mobileMeasureActions.setMovingPoint({ lineIndex: pointLineIndex, pointIndex }));
        mobileMeasureCursorRef.movingPoint = { lineIndex: pointLineIndex, pointIndex };
        teleportDpadToPoint(pointLineIndex, pointIndex);
        dispatch(mobileMeasureActions.setDpadStyle("normal"));
        dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
    };

    const { onPointerDown: handleMobilePointDown, onPointerUp: handleMobilePointUp } = useMobileLongPress<{
        pointLineIndex: number;
        pointIndex: number;
    }>({
        onTap: ({ pointLineIndex, pointIndex }) => handleMobilePointTap(pointLineIndex, pointIndex),
        onLongPress: ({ pointLineIndex, pointIndex }) => handleMobileLongPress(pointLineIndex, pointIndex),
    });

    const handlePointPointerDown = (
        event: React.PointerEvent<SVGSVGElement>,
        pointLineIndex: number,
        pointIndex: number,
    ) => {
        if (event.button !== 0) {
            return;
        }
        const startPos = vec2.fromValues(event.clientX, event.clientY);

        function stop(startDrag: boolean) {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
            if (startDrag) {
                dispatch(
                    pointLineActions.setDraggingPoint({
                        pointLineIdx: pointLineIndex,
                        pointIdx: pointIndex,
                        originalPicker: picker,
                    }),
                );
                dispatch(renderActions.setPicker(Picker.PointLine));
                return;
            }

            const isFirstPoint = pointIndex === 0;
            const isLastPoint = pointIndex === pointLines[pointLineIndex].points.length - 1;

            if (isFirstPoint && view) {
                dispatch(pointLineActions.reversePoints(pointLineIndex, view));
            }
            if (isFirstPoint || isLastPoint) {
                dispatch(pointLineActions.setCurrent(pointLineIndex));
                dispatch(renderActions.setPicker(Picker.PointLine));
            }
        }

        function onPointerMove(e: PointerEvent) {
            const pos = vec2.fromValues(e.clientX, e.clientY);
            const dist = vec2.dist(startPos, pos);
            if (dist >= 5) {
                stop(true);
            }
        }

        function onPointerUp(e: PointerEvent) {
            if (e.button !== 0) {
                return;
            }
            stop(false);
        }

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
    };

    return (
        <div ref={containerRef} onWheel={onWheel}>
            {computedPointLines.map(
                (
                    {
                        pointLine,
                        pointLabels,
                        lineLabels,
                        xAxisLabel,
                        yAxisLabel,
                        zAxisLabel,
                        isClosed,
                        centerLabelsPos2d,
                        menuPos2d,
                        totalLengthStr,
                    },
                    pointLineIdx,
                ) => {
                    const isCurrentLine = pointLineIdx === (draggingPoint ? draggingPoint.pointLineIdx : currentIndex);

                    return (
                        <div key={pointLineIdx}>
                            {/* Points */}
                            {pointLine.points.map((_point, pointIdx) => {
                                const isDragging =
                                    draggingPoint?.pointLineIdx === pointLineIdx && draggingPoint.pointIdx === pointIdx;
                                if (isDragging) {
                                    return null;
                                }
                                const isSnappedTo = isCurrentLine && pointIdx === snapTo;
                                if (isSnappedTo) {
                                    return null;
                                }
                                const id = `ptp-point-${pointLineIdx}-${pointIdx}`;
                                return (
                                    <PointElement
                                        key={id}
                                        id={id}
                                        onPointerDown={
                                            isDesktopPointLinePointerInteraction
                                                ? (e) => handlePointPointerDown(e, pointLineIdx, pointIdx)
                                                : () =>
                                                      handleMobilePointDown({
                                                          pointLineIndex: pointLineIdx,
                                                          pointIndex: pointIdx,
                                                      })
                                        }
                                        onPointerUp={
                                            isDesktopPointLinePointerInteraction ? undefined : handleMobilePointUp
                                        }
                                        style={{
                                            pointerEvents:
                                                draggingPoint ||
                                                (isPointLinePicker &&
                                                    isCurrentLine &&
                                                    pointIdx === 0 &&
                                                    (isDesktopPointLinePointerInteraction ||
                                                        pointLine.points.length > 1))
                                                    ? "none"
                                                    : undefined,
                                            touchAction: isDesktopPointLinePointerInteraction ? undefined : "none",
                                        }}
                                    />
                                );
                            })}

                            {/* Point labels and angles */}
                            {pointLabels &&
                                pointLabels.map((label, pointIdx) => {
                                    // When point is being snapped - no need to show it's label,
                                    // because there's already label of the point being snapped to.
                                    const snapping =
                                        isCurrentLine && draggingPoint?.pointIdx === pointIdx && snapTo !== null;
                                    if (snapping) {
                                        return null;
                                    }
                                    const id = `ptp-point-label-${pointLineIdx}-${pointIdx}`;
                                    return (
                                        <Fragment key={id}>
                                            {label.text && (
                                                <CanvasHudLineLabel
                                                    id={id}
                                                    bgColor={pointLabelBgColor}
                                                    textColor={pointLabelTextColor}
                                                >
                                                    {label.text}
                                                </CanvasHudLineLabel>
                                            )}
                                            {label.angleText && (
                                                <SimpleHudLabel
                                                    id={`ptp-point-angle-label-${pointLineIdx}-${pointIdx}`}
                                                    style={{
                                                        left: `${label.anglePosition2d?.[0] ?? -100}px`,
                                                        top: `${label.anglePosition2d?.[1] ?? -100}px`,
                                                    }}
                                                >
                                                    {label.angleText}
                                                </SimpleHudLabel>
                                            )}
                                        </Fragment>
                                    );
                                })}

                            {/* Axis labels */}
                            {xAxisLabel && (
                                <CanvasHudLineLabel
                                    id={`ptp-line-x-label-${pointLineIdx}`}
                                    bgColor="#570F0F"
                                    textColor="#F7D4DA"
                                >
                                    X: {xAxisLabel.text}
                                </CanvasHudLineLabel>
                            )}
                            {yAxisLabel && (
                                <CanvasHudLineLabel
                                    id={`ptp-line-y-label-${pointLineIdx}`}
                                    bgColor="#0D2959"
                                    textColor="#D2E0F9"
                                >
                                    Y: {yAxisLabel.text}
                                </CanvasHudLineLabel>
                            )}
                            {zAxisLabel && (
                                <CanvasHudLineLabel
                                    id={`ptp-line-z-label-${pointLineIdx}`}
                                    bgColor="#4D5500"
                                    textColor="#F9FFBB"
                                >
                                    Z: {zAxisLabel.text}
                                </CanvasHudLineLabel>
                            )}

                            {/* Line labels */}
                            {lineLabels.map((label, labelIdx) => {
                                const id = `ptp-line-label-${pointLineIdx}-${labelIdx}`;
                                const canDelete =
                                    isDesktopPointLinePointerInteraction &&
                                    (labelIdx === 0 || labelIdx === lineLabels.length - 1) &&
                                    !isClosed;
                                return (
                                    <CanvasHudLineLabel
                                        key={id}
                                        id={id}
                                        bgColor={labelBgColor}
                                        textColor={labelTextColor}
                                        onRemove={
                                            canDelete
                                                ? () =>
                                                      handleDeleteLineSegment(
                                                          dispatch,
                                                          view,
                                                          pointLine,
                                                          label.deletablePointId,
                                                      )
                                                : undefined
                                        }
                                    >
                                        {label.text}
                                    </CanvasHudLineLabel>
                                );
                            })}

                            {isDesktopPointLinePointerInteraction && pointLine.points.length > 2 && (
                                <LineMenu
                                    id={`ptp-line-menu-${pointLineIdx}`}
                                    pos={menuPos2d}
                                    onDelete={() => {
                                        handleDeletePointLine(dispatch, pointLine);
                                    }}
                                />
                            )}

                            {pointLine.points.length > 2 && pointLine.result && (
                                <SimpleHudLabel
                                    id={`ptp-line-total-length-${pointLineIdx}`}
                                    style={{
                                        left: `${centerLabelsPos2d?.[0] ?? -100}px`,
                                        top: `${centerLabelsPos2d?.[1] ?? -100}px`,
                                    }}
                                >
                                    {totalLengthStr}
                                </SimpleHudLabel>
                            )}
                        </div>
                    );
                },
            )}
        </div>
    );
});

const SimpleHudLabel = styled("div")`
    color: #e1e1e1;
    text-shadow:
        -1px -1px 0 #5b5b5b,
        1px -1px 0 #5b5b5b,
        -1px 1px 0 #5b5b5b,
        1px 1px 0 #5b5b5b;
    translate: -50% -50%;
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px; /* 133.333% */
    letter-spacing: 0.25px;
    transition: opacity 0.1s;
    position: absolute;
    pointer-events: none;
`;

function LineMenu({ id, pos, onDelete }: { id: string; pos: ReadonlyVec2 | undefined; onDelete: () => void }) {
    return (
        <OverlayMenuBox id={id} style={{ left: `${pos?.[0] ?? -100}px`, top: `${pos?.[1] ?? -100}px` }}>
            <OverlayMenuIconButton onClick={onDelete}>
                <TrashIcon />
            </OverlayMenuIconButton>
            <InfoButton />
        </OverlayMenuBox>
    );
}

function InfoButton() {
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const openWidget = useOpenWidget();
    const widgetKey = featuresConfig.pointLine.key;
    const isWidgetOpen = useAppSelector(selectWidgets).includes(widgetKey);

    if (isProcoreDesign || isWidgetOpen) {
        return;
    }

    return (
        <OverlayMenuIconButton onClick={() => openWidget(widgetKey, { force: true })}>
            <InfoIcon />
        </OverlayMenuIconButton>
    );
}

function PointElement(props: SVGProps<SVGSVGElement> & { id: string }) {
    return (
        <PointSvg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g />
            <circle cx="12" cy="12" r="3" fill={cursorColor} />
            <circle cx="12" cy="12" r="3" fill="#092A25" stroke={cursorColor} strokeWidth="2" />
        </PointSvg>
    );
}

const PointSvg = styled("svg")(
    () => css`
        position: absolute;
        translate: -12px -12px;
        & circle {
            r: 3px;
            transition: r 0.1s;
        }
        &:hover circle {
            r: 6px;
        }
        &:hover {
            cursor: grab;
        }
    `,
);

function updateComputedPointLines2d(
    view: View,
    width: number,
    height: number,
    computedPointLines: ComputedPointLine[],
) {
    const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false, clipFarPlane: true });

    const minAxisLabelYDistFromLineLabel = 20;

    const mouseSupport = hasMouseSupport();

    for (const line of computedPointLines) {
        line.points2d = line.pointLine.points.map((p) => toScreenSpace(p.position));
        line.snapInfo2d = line.pointLine.points.map(({ snap }) => getSnapInfo2d(view, toScreenSpace, snap));
        if (line.isClosed) {
            line.snapInfo2d.pop();
        }

        line.menuPos2d = toScreenSpace(line.center);
        if (line.menuPos2d) {
            line.centerLabelsPos2d = vec2.clone(line.menuPos2d);
            if (!mouseSupport) {
                line.centerLabelsPos2d[1] -= 30;
            }
        } else {
            line.centerLabelsPos2d = undefined;
        }

        line.lineLabels.forEach((label) => {
            updateLineLabel2d(label, view, toScreenSpace);
            ensureLabelIsOnScreen(label, width, height, 70);
        });

        for (const label of [line.xAxisLabel, line.yAxisLabel, line.zAxisLabel]) {
            if (!label) {
                continue;
            }
            updateLineLabel2d(label, view, toScreenSpace);
            ensureLabelIsOnScreen(label, width, height, 70);
            const firstLabel = line.lineLabels[0];
            if (
                label.position2d &&
                firstLabel.position2d &&
                Math.abs(label.position2d[0] - firstLabel.position2d[0]) < 50 &&
                Math.abs(label.position2d[1] - firstLabel.position2d[1]) < minAxisLabelYDistFromLineLabel
            ) {
                const offset =
                    label.position2d[1] > firstLabel.position2d[1]
                        ? minAxisLabelYDistFromLineLabel - (label.position2d[1] - firstLabel.position2d[1])
                        : -(minAxisLabelYDistFromLineLabel - (firstLabel.position2d[1] - label.position2d[1]));
                label.position2d[1] += offset;
            }
        }

        updatePointLabels2d(line);
        ensureMenuDoesntCoverPoint(line);
    }
}

function updateElementPositions(
    computedPointLines: ComputedPointLine[],
    container: HTMLDivElement,
    pointerPos: ReadonlyVec2 | null, // null for touch devices
    isDragging: boolean,
) {
    computedPointLines.forEach((line, idx) => {
        const {
            snapInfo2d,
            pointLabels,
            lineLabels,
            xAxisLabel,
            yAxisLabel,
            zAxisLabel,
            menuPos2d,
            centerLabelsPos2d,
        } = line;

        // Points
        snapInfo2d.forEach((point, pointIdx) => {
            const svgElement = container.querySelector(`#ptp-point-${idx}-${pointIdx}`) as SVGElement | null;
            if (!svgElement) {
                return;
            }

            if (svgElement) {
                svgElement.style.left = `${point?.connection[0] ?? -100}px`;
                svgElement.style.top = `${point?.connection[1] ?? -100}px`;
                const g = svgElement.querySelector("g");
                if (g) {
                    g.innerHTML = getPointElementGuidePaths(point);
                }
            }
        });

        // Point labels
        pointLabels?.forEach((label, labelIdx) => {
            const labelElement = container.querySelector(`#ptp-point-label-${idx}-${labelIdx}`) as HTMLElement | null;
            if (labelElement) {
                labelElement.style.left = `${label.position2d?.[0] ?? -100}px`;
                labelElement.style.top = `${label.position2d?.[1] ?? -100}px`;
            }

            const angleElement = container.querySelector(
                `#ptp-point-angle-label-${idx}-${labelIdx}`,
            ) as HTMLElement | null;
            if (angleElement) {
                angleElement.style.left = `${label.anglePosition2d?.[0] ?? -100}px`;
                angleElement.style.top = `${label.anglePosition2d?.[1] ?? -100}px`;
            }
        });

        // Line labels
        lineLabels.forEach((label, labelIdx) => {
            const labelElement = container.querySelector(`#ptp-line-label-${idx}-${labelIdx}`) as HTMLElement | null;
            if (!labelElement) {
                return;
            }

            labelElement.style.left = `${label.position2d?.[0] ?? -100}px`;
            labelElement.style.top = `${label.position2d?.[1] ?? -100}px`;
        });

        // Axis labels
        const axisLabels = [
            [xAxisLabel, "x"],
            [yAxisLabel, "y"],
            [zAxisLabel, "z"],
        ] as const;
        axisLabels.forEach(([label, axis]) => {
            if (!label) {
                return;
            }
            const labelElement = container.querySelector(`#ptp-line-${axis}-label-${idx}`) as HTMLElement | null;
            if (!labelElement) {
                return;
            }

            labelElement.style.left = `${label.position2d?.[0] ?? -100}px`;
            labelElement.style.top = `${label.position2d?.[1] ?? -100}px`;
        });

        // Menu
        const isMenuVisible = !pointerPos || getMenuVisibility(pointerPos, line, isDragging);
        const menuElement = container.querySelector(`#ptp-line-menu-${idx}`) as HTMLElement | null;
        if (menuElement) {
            menuElement.style.left = `${menuPos2d?.[0] ?? -100}px`;
            menuElement.style.top = `${menuPos2d?.[1] ?? -100}px`;
            toggleMenuVisibility(container, idx, isMenuVisible);
        }

        // Total length label
        const totalLengthElement = container.querySelector(`#ptp-line-total-length-${idx}`) as HTMLElement | null;
        if (totalLengthElement) {
            totalLengthElement.style.left = `${centerLabelsPos2d?.[0] ?? -100}px`;
            totalLengthElement.style.top = `${centerLabelsPos2d?.[1] ?? -100}px`;
            totalLengthElement.textContent = line.totalLengthStr;
            const visible = pointerPos ? !isMenuVisible : true;
            toggleCenterLabelsVisibility(container, idx, visible);
        }
    });
}

function getPointElementGuidePaths(snap: PointSnapInfo2d | undefined): string {
    if (snap) {
        const guides = getSnapGuides2d(snap);
        const offset = vec2.create();
        return guides
            .map((guide) => {
                if (!guide) {
                    return "";
                }
                vec2.scale(offset, guide, 9);
                return `
                        <path d="M12 12l${offset[0]} ${offset[1]}" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
                        <path d="M12 12l${offset[0]} ${offset[1]}" stroke="${cursorColor}" stroke-width="2" stroke-linecap="round"/>
                    `;
            })
            .join("");
    }

    return "";
}

function updatePointLabels2d(computedPointLine: ComputedPointLine) {
    const { pointLabels } = computedPointLine;
    pointLabels?.forEach((label, idx) => {
        const point = computedPointLine.snapInfo2d[idx]?.connection;
        if (!point) {
            label.position2d = undefined;
            label.anglePosition2d = undefined;
            return;
        }

        const dir = getPointLabelDir(computedPointLine, idx);

        const offset = calcMinDimensionalDist(dir, 50, 20);
        label.position2d = vec2.scaleAndAdd(vec2.create(), point, dir, offset); // Offset the label position

        let angleYOffset = 0;
        if (label.text) {
            angleYOffset = 20;

            // When label is placed above the point - put angle above the label.
            // Dot product is compared with -0.5 instead of 0, because if it's closer to 0
            // label is more on the side than above the point.
            if (vec2.dot(dir, [0, 1]) < -0.5) {
                angleYOffset *= -1;
            }
        }

        label.anglePosition2d = vec2.add(vec2.create(), label.position2d, [0, angleYOffset]);
    });
}

function ensureMenuDoesntCoverPoint(line: ComputedPointLine) {
    const { menuPos2d } = line;
    if (!menuPos2d) {
        return;
    }

    const minMenuToPointDist = 50;

    line.points2d.forEach((point2d, idx) => {
        if (!point2d) {
            return;
        }

        const dist = vec2.dist(menuPos2d, point2d);
        if (dist < minMenuToPointDist) {
            // Move menu away from the point
            const dir = getPointLabelDir(line, idx);
            vec2.negate(dir, dir);
            vec2.scaleAndAdd(menuPos2d, point2d, dir, minMenuToPointDist);
        }
    });
}

function getPointLabelDir(line: ComputedPointLine, idx: number): ReadonlyVec2 {
    const { pointLabels } = line;
    const point = line.snapInfo2d[idx]?.connection;
    if (!pointLabels || !point) {
        return vec2.fromValues(1, 0); // Default direction if no labels
    }

    // The idea is to position point labels based on the adjacent edges,
    // so the labels are as far from the edges as possible and not overlapping with them.
    // On a convex polygon they will also end up being on the outer side of the polygon.
    const prevIndex = line.isClosed && idx === 0 ? pointLabels.length - 1 : idx - 1;
    const prevPoint = line.snapInfo2d[prevIndex]?.connection;
    const nextIndex = line.isClosed && idx === pointLabels.length - 1 ? 0 : idx + 1;
    const nextPoint = line.snapInfo2d[nextIndex]?.connection;
    const dir = vec2.create();
    if (prevPoint && nextPoint) {
        vec2.sub(dir, nextPoint, prevPoint);
        vec2.normalize(dir, dir);
        const prevPointDir = vec2.sub(vec2.create(), prevPoint, point);
        vec2.normalize(prevPointDir, prevPointDir);
        const nextPointDir = vec2.sub(vec2.create(), nextPoint, point);
        vec2.normalize(nextPointDir, nextPointDir);
        const rotationDir = vec2.cross(vec3.create(), prevPointDir, nextPointDir)[2] > 0 ? 1 : -1;
        vec2.rotate(dir, dir, [0, 0], (Math.PI / 2) * rotationDir); // Rotate 90 degrees to get perpendicular direction
    } else if (prevPoint) {
        vec2.sub(dir, point, prevPoint);
        vec2.normalize(dir, dir);
    } else if (nextPoint) {
        vec2.sub(dir, point, nextPoint);
        vec2.normalize(dir, dir);
    } else {
        vec2.set(dir, 1, 0); // Default direction if no neighbors
    }

    return dir;
}

// Sometimes when changing camera we clone camera rotation,
// so rotation is not reliable to be used inside useMemo.
// Instead we use a ref to store the previous direction and compare it manually.
// This is needed to keep the same reference to not cause rebuilding computedPointLines unnecessarily.
function useOrthoCameraDir() {
    const camera = useAppSelector(selectCamera);

    const prevOrthoCameraDir = useRef<ReadonlyVec3 | null>(null);

    const orthoCameraRotation = camera.type === CameraType.Orthographic ? camera.goTo?.rotation : null;

    // getCameraDir is too cheap to put it inside useMemo
    let orthoCameraDir = orthoCameraRotation ? getCameraDir(orthoCameraRotation) : null;

    if (orthoCameraDir && prevOrthoCameraDir.current && vec3.exactEquals(orthoCameraDir, prevOrthoCameraDir.current)) {
        // If the orthographic camera direction hasn't changed, we can use the previous direction
        // to preserve reference and not rebuild computedPointLines unnecessarily.
        orthoCameraDir = prevOrthoCameraDir.current;
    } else {
        prevOrthoCameraDir.current = orthoCameraDir;
    }

    return orthoCameraDir;
}

function getMenuVisibility(pos: ReadonlyVec2, line: ComputedPointLine, isDragging: boolean) {
    return Boolean(
        !isDragging &&
        line.menuPos2d &&
        (vec2.sqrDist(line.menuPos2d, pos) <= MENU_VISIBLITY_SQR_DIST ||
            isPoint2dInsidePolygon(pos[0], pos[1], line.points2d.filter(Boolean) as ReadonlyVec2[])),
    );
}

function toggleMenuVisibility(container: HTMLElement, lineIndex: number, visible: boolean) {
    const node = container.querySelector(`#ptp-line-menu-${lineIndex}`) as HTMLElement;
    if (node) {
        node.style.opacity = visible ? "1" : "0";
        node.style.pointerEvents = visible ? "auto" : "none";
    }
}

function toggleCenterLabelsVisibility(container: HTMLElement, lineIndex: number, visible: boolean) {
    const totalLengthElement = container.querySelector(`#ptp-line-total-length-${lineIndex}`) as HTMLElement | null;
    if (totalLengthElement) {
        totalLengthElement.style.opacity = visible ? "1" : "0";
    }
}
